import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as S from './styles';
import Texts from '../Texts';
import ArrowBackWardIcon from '@mui/icons-material/ArrowCircleRight';
import { MenuItem, CircularProgress } from '@mui/material';
import apiClient from '../../api/apiClient';
import { validationSchemaStepOne } from './validation';
import { useNavigate } from 'react-router-dom';
import {
  updateIdentityField,
  updateApplicationField,
  setError,
} from '../../Slice/unifiedFormSlice';
import { toast } from 'react-toastify';
import CancelButton from '../CommanButton/CancelButton';
import { RootState } from '../../store';

interface propsApplicationStepOne {
  id: string;
  display_name: string;
}

const ApplicationStepOne: React.FC<propsApplicationStepOne> = ({
  id,
  display_name,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { application, identity } = useSelector(
    (state: RootState) => state.unifiedForm,
  );
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // تنظیم id و display_name در Redux اگر خالی باشند
  useEffect(() => {
    if (id && !identity.id) {
      dispatch(updateIdentityField({ field: 'id', value: Number(id) }));
      dispatch(updateApplicationField({ field: 'publisher_id', value: id }));
    }
    if (display_name && !identity.display_name) {
      dispatch(
        updateIdentityField({ field: 'display_name', value: display_name }),
      );
    }
  }, [dispatch, id, display_name, identity.id, identity.display_name]);

  // فراخوانی دسته‌بندی‌ها
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(
          '/v2/publisher/applications/category',
        );
        if (response.data && Array.isArray(response.data.categories)) {
          setCategories(response.data.categories);
        } else {
          toast.error('Invalid data format received from the API');
        }
      } catch (err: any) {
        console.error('Error fetching categories:', err);
        setError(err.message || 'خطا در دریافت داده‌ها');
        toast.error(err.message || 'خطا در دریافت داده‌ها');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const formik = useFormik({
    initialValues: {
      fa_name: application.fa_name || '',
      en_name: application.en_name || '',
      category: application.category || '',
    },
    validationSchema: validationSchemaStepOne,
    onSubmit: (values) => {
      // اختیاری: در صورت نیاز به submit دستی
    },
  });

  // به‌روزرسانی بلادرنگ Redux state
  useEffect(() => {
    dispatch(
      updateApplicationField({
        field: 'fa_name',
        value: formik.values.fa_name,
      }),
    );
    dispatch(
      updateApplicationField({
        field: 'en_name',
        value: formik.values.en_name,
      }),
    );
    dispatch(
      updateApplicationField({
        field: 'category',
        value: formik.values.category,
      }),
    );
  }, [
    formik.values.fa_name,
    formik.values.en_name,
    formik.values.category,
    dispatch,
  ]);

  return (
    <S.SBox>
      <S.Form onSubmit={formik.handleSubmit}>
        <S.Title as="div">{Texts.application.titel}</S.Title>

        <S.SubTitle as="div">
          اپلیکیشن شما به نام{' '}
          <strong>{identity.display_name || display_name}</strong> ثبت خواهد شد.
        </S.SubTitle>

        {/* <S.Label>شناسه ناشر: {identity.id || id}</S.Label> */}

        <S.Label>{Texts.application.lablefa}</S.Label>
        <S.TextFieldWrapper>
          <S.TextFieldBackground />
          <S.STextField
            sx={{ direction: 'rtl' }}
            id="fa_name"
            name="fa_name"
            type="text"
            placeholder={Texts.application.placeholderfa}
            value={formik.values.fa_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.fa_name && formik.errors.fa_name && (
            <div style={{ color: 'red', fontSize: '12px' }}>
              {formik.errors.fa_name}
            </div>
          )}
        </S.TextFieldWrapper>

        <S.Label>{Texts.application.lableen}</S.Label>
        <S.TextFieldWrapper>
          <S.TextFieldBackground />
          <S.STextField
            id="en_name"
            name="en_name"
            type="text"
            placeholder={Texts.application.placeholderen}
            value={formik.values.en_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.en_name && formik.errors.en_name && (
            <div style={{ color: 'red', fontSize: '12px' }}>
              {formik.errors.en_name}
            </div>
          )}
        </S.TextFieldWrapper>

        <S.Label>{Texts.application.category}</S.Label>
        <S.TextFieldWrapper>
          <S.TextFieldBackground />
          {loading ? (
            <CircularProgress size={24} />
          ) : error ? (
            <div style={{ color: 'red' }}>{error}</div>
          ) : (
            <S.SelectField
              name="category"
              value={formik.values.category}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              displayEmpty
              inputProps={{ 'aria-label': 'Select category' }}
            >
              <MenuItem value="" disabled>
                بدون دسته‌بندی
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </S.SelectField>
          )}
          {formik.touched.category && formik.errors.category && (
            <div style={{ color: 'red', fontSize: '12px' }}>
              {formik.errors.category}
            </div>
          )}
        </S.TextFieldWrapper>
      </S.Form>
    </S.SBox>
  );
};

export default ApplicationStepOne;
